import { z } from 'zod';
import { paletteSchema, neutralColorPresetSchema } from './colors.ts';
import { idSchema, localeSchema, imageSourceSchema, siteImageSchema } from './content-primitives.ts';
import { dateOrTimeSchema } from './content-primitives.ts';
import type { SubmissionAction } from './form-actions.ts';
import { additionalObjectsSchema, siteAuthorsSchema, pageObjectSchema } from './json-ld.ts';
import { siteOrganizationSchema } from './json-ld.ts';
import { siteAssistantQuickActionsSchema } from './site-assistant.ts';
import {
  footerStyleSchema,
  sectionStyleSchema,
  columnStyleSchema,
  headerStyleSchema,
  imageStyleSchema,
  iconStyleSchema,
  formStyleSchema,
  markdownStyleSchema,
} from './styles.ts';

export const linkTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('Page'), pageId: idSchema }).strict(),
  z.object({ kind: z.literal('Url'), url: z.string() }).strict(),
]);
export const buttonLinkSchema = z.strictObject({
  text: z.string().trim().optional(),
  label: z
    .string()
    .trim()
    .optional()
    .describe(
      'Required when text is omitted. Used as the icon-only button’s accessible name and as its link text in Markdown.',
    ),
  icon: z.string().trim().optional(),
  iconPosition: z.enum(['Left', 'Right']).optional(),
  target: linkTargetSchema,
  newTab: z.boolean(),
  variant: z.enum(['Primary', 'Soft', 'Ghost', 'Custom']),
  size: z.enum(['Small', 'Medium', 'Large']).optional().describe('Button size. Defaults to Medium.'),
  style: z
    .strictObject({
      color: z.string().optional(),
      background: z.string().optional(),
      border: z.string().optional(),
    })
    .optional()
    .describe('CSS overrides for the Custom variant, which inherits Primary styling.'),
});
export type ButtonLink = z.infer<typeof buttonLinkSchema>;

export const navigationLinkSchema = z.strictObject({
  label: z.string().trim().min(1).max(32),
  target: linkTargetSchema,
});
const headerDropdownSchema = z
  .strictObject({
    label: navigationLinkSchema.shape.label,
    links: z.array(navigationLinkSchema).min(1).max(18),
  })
  .describe('A dropdown with a label and child links. The trigger has no link destination.');
export type NavigationLink = z.infer<typeof navigationLinkSchema>;
export const headerLogoSizeSchema = z.enum(['Small', 'Medium', 'Large', 'XLarge']);
export const headerLocaleSchema = z.strictObject({
  navigation: z.array(z.union([navigationLinkSchema, headerDropdownSchema])).max(7),
  cta: navigationLinkSchema.optional(),
});
export const footerLocaleSchema = z.strictObject({
  information: z.string(),
  columns: z.array(z.strictObject({ id: idSchema, links: z.array(navigationLinkSchema).max(7) })).max(3),
  links: z.array(navigationLinkSchema).max(20),
});
export const socialLinkSchema = z
  .object({
    platform: z.enum([
      'facebook',
      'instagram',
      'x',
      'linkedin',
      'youtube',
      'tiktok',
      'pinterest',
      'github',
      'threads',
      'bluesky',
      'other',
    ]),
    url: z.url(),
  })
  .strict();
const sitePathSchema = z
  .string()
  .max(300)
  .regex(/^\/[^%?#]*$/)
  .refine(
    (path) =>
      !path.startsWith('//') &&
      !path.includes('\\') &&
      !path.split('/').some((p) => p === '.' || p === '..') &&
      ![...path].some((c) => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127),
    'Use an absolute site-relative path without dot segments, backslashes, or control characters.',
  );
export const redirectSchema = z
  .object({
    from: sitePathSchema.describe('The site-relative path that redirects, including a leading slash.'),
    to: z
      .string()
      .trim()
      .max(2000)
      .refine(
        (to) =>
          ![...to].some((c) => c.charCodeAt(0) <= 32 || c.charCodeAt(0) === 127) &&
          !to.includes('\\') &&
          (to.startsWith('/')
            ? !to.startsWith('//') && URL.canParse(to, 'https://example.com')
            : /^https?:\/\//i.test(to) && URL.canParse(to)),
        'Use a root-relative path starting with / or a full http or https URL.',
      )
      .describe(
        'A root-relative path or a full URL. The visitor’s query string is kept unless this target has its own.',
      ),
    status: z.literal([301, 302]).describe('301 for a permanent move; 302 for a temporary one.'),
  })
  .strict();

export const siteSettingsSchema = z
  .object({
    primaryColorPalette: paletteSchema.optional(),
    secondaryColorPalette: paletteSchema.optional(),
    neutralColorPreset: neutralColorPresetSchema.optional(),
    name: z.string().max(200),
    alternateNames: z.array(z.string().trim().min(1).max(200)).max(20),
    baseLocale: localeSchema,
    locales: z
      .array(
        z
          .object({
            locale: localeSchema,
            slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
          })
          .strict(),
      )
      .min(1),
    logo: siteImageSchema,
    headerLogoSize: headerLogoSizeSchema,
    favicon: siteImageSchema,
    headerStyle: headerStyleSchema.optional(),
    footerStyle: footerStyleSchema.optional(),
    header: z.record(localeSchema, headerLocaleSchema),
    footer: z.record(localeSchema, footerLocaleSchema),
    copyright: z
      .object({
        enabled: z.boolean().describe('When true, the footer shows a copyright line.'),
        text: z
          .string()
          .max(200)
          .describe(
            'If omitted, uses the site name. The footer prefixes this with Copyright © and the current year; do not include a year or copyright mark.',
          )
          .optional(),
      })
      .strict(),
    headStartCode: z.string(),
    bodyStartCode: z.string(),
    bodyEndCode: z.string(),
    organization: siteOrganizationSchema.optional(),
    authors: siteAuthorsSchema,
    postCollections: z
      .array(z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/))
      .min(1)
      .refine((values) => new Set(values).size === values.length, 'Collection names must be unique.'),
    businessOverview: z.string().trim().max(30_000).optional(),
    siteAssistantFormId: idSchema.optional(),
    siteAssistantKnowledge: z.string().trim().max(30_000).optional(),
    siteAssistantQuickActions: siteAssistantQuickActionsSchema.optional(),
    blogAutomationInstructions: z.string().max(30_000).optional(),
    blogAutomationPostCollection: z.string().optional(),
    indexNowKey: z
      .string()
      .trim()
      .regex(/^([a-zA-Z0-9-]{8,128})?$/, 'Use 8–128 letters, numbers, or dashes, or leave empty to disable.')
      .optional(),
    socialLinks: z.array(socialLinkSchema).max(100),
    redirects: z
      .array(redirectSchema)
      .max(1000)
      .describe(
        'Exact-path redirects on the published site. Each from path must be unique and not used by a page.',
      ),
  })
  .strict();
export const iconSizeSchema = z.literal([24, 32, 48, 64, 96]).describe('Width and height in pixels.');
export const contentItemSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('Markdown'),
      style: markdownStyleSchema.optional(),
      markdown: z.string(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('HTML'),
      html: z.string().describe('Arbitrary HTML rendered verbatim in the page body.'),
      markdown: z.string().optional().describe('Optional content for the Markdown version of the page.'),
    })
    .strict(),
  z
    .object({
      kind: z.literal('Image'),
      style: imageStyleSchema.optional(),
      source: imageSourceSchema,
      alt: z.string().max(1000),
      caption: z.string().max(2000).optional(),
    })
    .strict(),
  z
    .object({
      kind: z.literal('Icon'),
      style: iconStyleSchema.optional(),
      icon: z.string(),
      size: iconSizeSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('Form'),
      style: formStyleSchema.optional(),
      formId: idSchema,
    })
    .strict(),
  z
    .object({
      kind: z.literal('ReviewForm'),
      googleReviewUrl: z
        .url()
        .describe(
          'URL for leaving a Google review. Use https://search.google.com/local/writereview?placeid=PLACE_ID, replacing PLACE_ID with the business’s Google Place ID.',
        ),
      googleReviewMinimumStars: z
        .literal([1, 2, 3, 4, 5])
        .describe('Minimum stars to show the Google review link, inclusive.'),
    })
    .strict(),
  z
    .object({
      kind: z.literal('PostList'),
      collection: z.string().describe('A collection name from site settings.postCollections'),
    })
    .strict(),
  z.strictObject({
    kind: z.literal('Buttons'),
    alignment: z.enum(['Left', 'Center', 'Right']),
    buttons: z.array(buttonLinkSchema).min(1).max(2),
  }),
]);
export const sectionSchema = z
  .object({
    style: sectionStyleSchema.optional(),
    anchor: z
      .string()
      .regex(
        /^[a-zA-Z][a-zA-Z0-9_-]{0,79}$/,
        'Use letters, numbers, hyphens or underscores, beginning with a letter.',
      )
      .optional()
      .describe('Sets this section’s HTML "id" on the published page.'),
    columns: z
      .array(
        z
          .object({
            style: columnStyleSchema.optional(),
            items: z.array(contentItemSchema),
          })
          .strict(),
      )
      .min(1)
      .max(3),
  })
  .strict();
export const pageSchema = z
  .object({
    title: z
      .string()
      .trim()
      .max(120)
      .regex(/^[^\r\n]*$/, 'Remove line breaks from the title.')
      .meta({ pattern: undefined }),
    path: sitePathSchema.describe(
      'Full site-relative path, including a leading slash. Non-base locales require their configured locale prefix.',
    ),
    pageObject: pageObjectSchema.optional(),
    modifiedAt: dateOrTimeSchema
      .optional()
      .describe(
        'Overrides the last-modified date. When omitted, uses the first publication of this exact page version.',
      ),
    additionalObjects: additionalObjectsSchema,
    body: z.array(sectionSchema),
    metaTitle: z.string().trim().max(100),
    metaDescription: z.string().trim().max(200),
    metaImage: imageSourceSchema.optional(),
    noIndex: z.boolean(),
    archived: z.boolean(),
    locale: localeSchema.describe('The language of this page. Must be one of the site’s enabled locales.'),
    translationGroup: idSchema.describe('Language variants of one page share this ID.'),
  })
  .strict();
export const postSchema = pageSchema.extend({
  collection: z.string().describe('A collection name from site settings.postCollections'),
  publishedAt: z.iso.datetime(),
  summary: z.string().max(2000).optional(),
  image: imageSourceSchema.optional(),
});

export const fieldIdentifierSchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,63}$/)
  .refine(
    (name) => !['constructor', 'prototype', '__proto__'].includes(name),
    'Choose a non-reserved field identifier.',
  );
const formFieldBaseSchema = z.strictObject({
  name: fieldIdentifierSchema,
  required: z.boolean(),
});
const formTextFieldSchema = formFieldBaseSchema
  .extend({
    minLength: z.number().int().nonnegative().max(100_000).optional(),
    maxLength: z.number().int().positive().max(100_000).optional(),
  })
  .superRefine((field, ctx) => {
    if (field.minLength !== undefined && field.maxLength !== undefined && field.minLength > field.maxLength)
      ctx.addIssue({
        code: 'custom',
        path: ['minLength'],
        message: 'Minimum length must not exceed maximum length.',
      });
  });
const formNumberFieldSchema = formFieldBaseSchema
  .extend({ minimum: z.number().optional(), maximum: z.number().optional() })
  .superRefine((field, ctx) => {
    if (field.minimum !== undefined && field.maximum !== undefined && field.minimum > field.maximum)
      ctx.addIssue({ code: 'custom', path: ['minimum'], message: 'Minimum must not exceed maximum.' });
  });
const formOptionsSchema = z
  .array(
    z.strictObject({
      value: fieldIdentifierSchema,
      label: z.record(localeSchema, z.string().min(1).max(300)),
    }),
  )
  .refine(
    (options) => new Set(options.map((option) => option.value)).size === options.length,
    'Option values must be unique.',
  );
export const formFieldSchema = z.discriminatedUnion('type', [
  formTextFieldSchema.safeExtend({ type: z.literal('Text') }),
  formTextFieldSchema.safeExtend({ type: z.literal('TextArea') }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Email') }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Tel') }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Url') }),
  formNumberFieldSchema.safeExtend({ type: z.literal('Number') }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Date') }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Time') }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Select'), options: formOptionsSchema }),
  formFieldBaseSchema.safeExtend({ type: z.literal('RadioGroup'), options: formOptionsSchema }),
  formFieldBaseSchema.safeExtend({ type: z.literal('CheckboxGroup'), options: formOptionsSchema }),
  formFieldBaseSchema.safeExtend({ type: z.literal('Checkbox') }),
]);
export const formLocalizationSchema = z.strictObject({
  title: z.string().min(1).max(200),
  submitLabel: z.string().min(1).max(100),
  confirmation: z.string(),
  errors: z.strictObject({
    required: z.string().min(1),
    invalid: z.string().min(1),
    tooShort: z.string().min(1),
    tooLong: z.string().min(1),
    range: z.string().min(1),
  }),
});
const formInputItemSchemas = formFieldSchema.options.map((schema) =>
  schema.safeExtend({
    localizations: z.record(
      localeSchema,
      z.strictObject({ label: z.string().min(1).max(400), help: z.string().max(1000) }),
    ),
  }),
);
const formMarkdownItemSchema = z.strictObject({
  type: z.literal('Markdown'),
  markdown: z.record(localeSchema, z.string()),
});
const formBodySchema = z
  .array(z.discriminatedUnion('type', [formMarkdownItemSchema, ...formInputItemSchemas]))
  .superRefine((items, ctx) => {
    const inputs = items.filter((item) => item.type !== 'Markdown');
    if (new Set(inputs.map((item) => item.name)).size !== inputs.length)
      ctx.addIssue({ code: 'custom', message: 'Field names must be unique.' });
  });
export const formContentSchema = z.strictObject({
  body: formBodySchema,
  localizations: z.record(localeSchema, formLocalizationSchema),
});
export const formSchema = formContentSchema.extend({
  name: z.string().min(1).max(200),
  toolName: z.string().regex(/^submit_[a-z0-9_]{1,56}$/),
  description: z.string().min(1).max(1000),
  showTitle: z.boolean(),
  spamCheck: z.boolean(),
  archived: z.boolean(),
});

export const imageAssetSchema = z
  .object({
    alt: z.record(localeSchema, z.string().max(50_000)),
    archived: z.boolean(),
  })
  .strict();
/** Facts about the uploaded file; the server sets them at import and they never change. */
export const assetFileSchema = z.object({
  fileName: z.string(),
  mimeType: z.string(),
  byteSize: z.number().int(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  url: z.url(),
});
export type AssetFile = z.infer<typeof assetFileSchema>;
export const assetFileKeys = Object.keys(assetFileSchema.shape) as (keyof AssetFile)[];

export const recordSchemas = {
  Settings: siteSettingsSchema,
  Page: pageSchema,
  Post: postSchema,
  Form: formSchema,
  Asset: imageAssetSchema,
};
export type SiteSettings = z.infer<typeof siteSettingsSchema>;
export type Redirect = z.infer<typeof redirectSchema>;
export type Page = z.infer<typeof pageSchema>;
export type Form = z.infer<typeof formSchema>;
export type FormField = z.infer<typeof formFieldSchema>;
export type FormInputItem = z.infer<(typeof formInputItemSchemas)[number]>;
/** Kinds handled by the site-content record pipeline. */
export type RecordKind = keyof typeof recordSchemas;
/** Keys the server assigns to every saved record. Everything else, including kind, is editable. */
export const readOnlyKeys = ['id', 'versionId', 'createdAt', 'isPublished'] as const;
export type ReadOnlyKey = (typeof readOnlyKeys)[number];
export type RecordEnvelope = { id: string; versionId: string; createdAt: string; isPublished: boolean };
export type PageBody<M = string> = Array<
  Omit<Page['body'][number], 'columns'> & {
    columns: Array<
      Omit<Page['body'][number]['columns'][number], 'items'> & {
        items: Array<
          | Exclude<z.infer<typeof contentItemSchema>, { kind: 'Markdown' }>
          | (Omit<Extract<z.infer<typeof contentItemSchema>, { kind: 'Markdown' }>, 'markdown'> & {
              markdown: M;
            })
        >;
      }
    >;
  }
>;
export type KindFields<K extends RecordKind, F extends Form, M> = K extends 'Form'
  ? F
  : K extends 'Asset'
    ? z.infer<typeof imageAssetSchema> & AssetFile
    : K extends 'Page' | 'Post'
      ? Omit<z.infer<(typeof recordSchemas)[K]>, 'body'> & { body: PageBody<M> }
      : z.infer<(typeof recordSchemas)[K]>;
/** All versioned editable resources, including form submission actions. */
export type ContentObject<F extends Form = Form, M = string> =
  | { [K in RecordKind]: RecordEnvelope & { kind: K } & KindFields<K, F, M> }[RecordKind]
  | SubmissionAction;
/** Resources supported by recordSchemas and the generic read/save pipeline. */
export type RecordObject<F extends Form = Form, M = string> = Extract<
  ContentObject<F, M>,
  { kind: RecordKind }
>;
export type SavedSiteSettings = SiteSettings & {
  siteId: string;
  versionId: string;
  createdAt: Date;
  isPublished: boolean;
};
/** Complete site records grouped by resource type. */
export type SiteRecords<P = Extract<RecordObject, { kind: 'Page' | 'Post' }>> = {
  siteId: string;
  settings: SavedSiteSettings;
  pages: P[];
  forms: Extract<RecordObject, { kind: 'Form' }>[];
  assets: Extract<RecordObject, { kind: 'Asset' }>[];
};
/** Resources consumed by rendering and its snapshots. */
export const renderContentKinds = [
  'Settings',
  'Page',
  'Post',
  'Form',
  'Asset',
] as const satisfies readonly ContentObject['kind'][];
export type RenderContentObject<F extends Form = Form, M = string> = Extract<
  ContentObject<F, M>,
  { kind: (typeof renderContentKinds)[number] }
>;
/** The editable part of a record: its kind and fields, without server-assigned keys. */
export type WritableRecord<T extends { kind: string } = RecordObject> = T extends {
  kind: 'Asset';
}
  ? Omit<T, ReadOnlyKey | keyof AssetFile>
  : T extends unknown
    ? Omit<T, ReadOnlyKey>
    : never;
export const readOnlyKeysOf = (kind: string): readonly string[] =>
  kind === 'Asset' ? [...readOnlyKeys, ...assetFileKeys] : readOnlyKeys;
export function writable<T extends { kind: string }>(object: T): WritableRecord<T> {
  const excluded = readOnlyKeysOf(object.kind);
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => !excluded.includes(key)),
  ) as WritableRecord<T>;
}
/** The writable fields without kind: exactly what recordSchemas[kind] validates. */
export type RecordFields<T extends { kind: string } = RecordObject> = T extends unknown
  ? Omit<WritableRecord<T>, 'kind'>
  : never;
export function recordFields<T extends { kind: string }>(object: T): RecordFields<T> {
  const { kind: _kind, ...fields } = writable(object);
  return fields as RecordFields<T>;
}
/** The complement of writable(): the server-assigned keys of a record. */
export function readOnlyFields(object: RecordObject): RecordEnvelope & Partial<AssetFile> {
  const included = readOnlyKeysOf(object.kind);
  return Object.fromEntries(
    Object.entries(object).filter(([key]) => included.includes(key)),
  ) as RecordEnvelope & Partial<AssetFile>;
}
/** Site settings are the one record kind that cannot be archived. */
export const isArchived = (object: { kind: string; archived?: boolean }): boolean =>
  object.kind !== 'Settings' && object.archived === true;
export const recordKindSchema = z.enum(Object.keys(recordSchemas) as [RecordKind, ...RecordKind[]]);
export function parseRecordObject(input: {
  id: string;
  versionId: string;
  createdAt: Date | string;
  isPublished: boolean;
  kind: string;
  [field: string]: unknown;
}): RecordObject {
  const { id, versionId, createdAt, isPublished, kind: rawKind, ...fields } = input;
  const kind = recordKindSchema.parse(rawKind);
  const file = kind === 'Asset' ? assetFileSchema.parse(fields) : {};
  for (const key of kind === 'Asset' ? assetFileKeys : []) delete fields[key];
  return {
    id,
    versionId,
    createdAt: createdAt instanceof Date ? createdAt.toISOString() : createdAt,
    isPublished,
    kind,
    ...file,
    ...recordSchemas[kind].parse(fields),
  } as RecordObject;
}
/** Rendering content also used by the visual editor and content quality checks. */
export type SiteSnapshot<F extends Form = Form, M = string> = {
  siteId: string;
  objects: RenderContentObject<F, M>[];
};
export type Content = RenderContentObject;
